// frontend/src/components/UI/NotificationDropdown.js
/**
 * Modern SaaS-style Notification Dropdown
 *
 * Features:
 * - Compact, polished design with smooth animations
 * - "New" and "Earlier" grouping
 * - Blue dot unread indicator + subtle tinted background
 * - Hover actions (mark read, dismiss)
 * - Beautiful empty state with illustration
 * - Escape key to close (click-outside lives on the parent
 *   wrapper that includes the bell trigger — DS dismissal pattern)
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { safeNavigateToNotificationUrl } from '../../utils/urlUtils';
import { 
  X, 
  Check, 
  Trash2, 
  Clock, 
  AlertTriangle, 
  CheckCircle2, 
  Info, 
  FileText, 
  Calendar,
  Wallet,
  UserCheck,
  Megaphone,
  Inbox,
  Settings
} from 'lucide-react';

const NotificationDropdown = ({ 
  notifications = [], 
  unreadCount = 0, 
  loading = false, 
  error = null, 
  onClose, 
  onMarkAsRead, 
  onMarkAllAsRead,
  onDismiss // Optional: for dismissing/deleting notifications
}) => {
  const navigate = useNavigate();
  const [hoveredId, setHoveredId] = useState(null);

  // Escape key to close
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Group notifications by "New" (unread) and "Earlier" (read)
  const groupedNotifications = React.useMemo(() => {
    const newItems = notifications.filter(n => !n.is_read);
    const earlierItems = notifications.filter(n => n.is_read);
    return { newItems, earlierItems };
  }, [notifications]);

  // Get sophisticated icon with colored background
  const getNotificationIcon = (iconType, category) => {
    const iconConfigs = {
      success: {
        bg: 'bg-emerald-50',
        iconColor: 'text-emerald-500',
        Icon: CheckCircle2
      },
      warning: {
        bg: 'bg-amber-50',
        iconColor: 'text-amber-500',
        Icon: AlertTriangle
      },
      error: {
        bg: 'bg-red-50',
        iconColor: 'text-red-500',
        Icon: AlertTriangle
      },
      info: {
        bg: 'bg-blue-50',
        iconColor: 'text-blue-500',
        Icon: Info
      }
    };

    // Category-specific icons
    const categoryIcons = {
      leave: Calendar,
      attendance: Clock,
      documents: FileText,
      payroll: Wallet,
      employment: UserCheck,
      system: Megaphone,
      compliance: AlertTriangle
    };

    const config = iconConfigs[iconType] || iconConfigs.info;
    const CategoryIcon = categoryIcons[category] || config.Icon;

    return (
      <div className={`w-9 h-9 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}>
        <CategoryIcon className={`w-4 h-4 ${config.iconColor}`} />
      </div>
    );
  };

  // Handle notification click
  const handleNotificationClick = (notification, e) => {
    // Don't navigate if clicking on action buttons
    if (e.target.closest('[data-action]')) return;

    if (!notification.is_read && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
    
    if (notification.action_url) {
      safeNavigateToNotificationUrl(notification.action_url, navigate);
      onClose?.();
    }
  };

  // Handle mark as read
  const handleMarkAsRead = (e, notificationId) => {
    e.stopPropagation();
    onMarkAsRead?.(notificationId);
  };

  // Handle dismiss
  const handleDismiss = (e, notificationId) => {
    e.stopPropagation();
    onDismiss?.(notificationId);
  };

  // Render notification item
  const NotificationItem = ({ notification }) => {
    const isHovered = hoveredId === notification.id;
    
    return (
      <div
        className={`
          group relative px-4 py-3 cursor-pointer transition-all duration-150
          ${!notification.is_read 
            ? 'bg-blue-50/50 hover:bg-blue-50/80' 
            : 'bg-white hover:bg-gray-50'
          }
        `}
        onClick={(e) => handleNotificationClick(notification, e)}
        onMouseEnter={() => setHoveredId(notification.id)}
        onMouseLeave={() => setHoveredId(null)}
      >
        <div className="flex items-start gap-3">
          {/* Icon */}
          {getNotificationIcon(notification.icon_type, notification.category)}
          
          {/* Content */}
          <div className="flex-1 min-w-0 pr-6">
            {/* Title with unread dot */}
            <div className="flex items-center gap-2">
              <p className={`text-sm font-medium leading-tight truncate ${
                !notification.is_read ? 'text-gray-900' : 'text-gray-600'
              }`}>
                {notification.title}
              </p>
              {!notification.is_read && (
                <span className="w-2 h-2 bg-blue-900 rounded-full flex-shrink-0" />
              )}
            </div>
            
            {/* Message - compact */}
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 leading-relaxed">
              {notification.message}
            </p>
            
            {/* Timestamp - smaller */}
            <p className="text-[11px] text-gray-400 mt-1.5">
              {notification.time_ago}
            </p>
          </div>

          {/* Hover Actions */}
          <div 
            className={`
              absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1
              transition-opacity duration-150
              ${isHovered ? 'opacity-100' : 'opacity-0'}
            `}
          >
            {!notification.is_read && (
              <button
                data-action="mark-read"
                onClick={(e) => handleMarkAsRead(e, notification.id)}
                className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white shadow-sm border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
                title="Mark as read"
              >
                <Check className="w-3.5 h-3.5 text-gray-500" />
              </button>
            )}
            {onDismiss && (
              <button
                data-action="dismiss"
                onClick={(e) => handleDismiss(e, notification.id)}
                className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white shadow-sm border border-gray-200 hover:bg-red-50 hover:border-red-200 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
                title="Dismiss"
              >
                <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Section header
  const SectionHeader = ({ title }) => (
    <div className="px-4 py-2 bg-gray-50/80 border-b border-gray-100">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
        {title}
      </p>
    </div>
  );

  // Empty state with illustration
  const EmptyState = () => (
    <div className="py-12 px-6 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center">
        <Inbox className="w-8 h-8 text-emerald-400" />
      </div>
      <h4 className="text-base font-semibold text-gray-800 mb-1">
        All caught up!
      </h4>
      <p className="text-sm text-gray-500 max-w-[200px] mx-auto">
        You have no new notifications. Check back later.
      </p>
    </div>
  );

  // Loading state
  const LoadingState = () => (
    <div className="py-12 px-6 text-center">
      <div className="w-8 h-8 mx-auto mb-3">
        <div className="w-full h-full rounded-full border-2 border-gray-200 border-t-blue-900 animate-spin" />
      </div>
      <p className="text-sm text-gray-500">Loading notifications...</p>
    </div>
  );

  // Error state
  const ErrorState = () => (
    <div className="py-12 px-6 text-center">
      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-50 flex items-center justify-center">
        <AlertTriangle className="w-6 h-6 text-red-400" />
      </div>
      <p className="text-sm font-medium text-gray-800 mb-1">Something went wrong</p>
      <p className="text-xs text-gray-500">{error || 'Failed to load notifications'}</p>
    </div>
  );

  return (
    <div 
      className="fixed sm:absolute right-2 sm:right-0 top-16 sm:top-12 w-[calc(100vw-16px)] sm:w-[360px] max-w-[360px] bg-white rounded-xl shadow-xl border border-gray-200/80 z-50 overflow-hidden"
      style={{ 
        boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
        maxHeight: 'calc(100dvh - 80px)', // dvh for iOS Safari viewport handling
        paddingBottom: 'env(safe-area-inset-bottom, 0)' // iPhone notch safety
      }}
      role="dialog"
      aria-label="Notifications"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <span className="px-1.5 py-0.5 text-[10px] font-bold text-white bg-blue-900 rounded-full min-w-[18px] text-center">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => onMarkAllAsRead?.()}
                className="icon-button"
                title="Mark all as read"
                aria-label="Mark all as read"
              >
                <Check className="w-5 h-5" />
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                navigate('/notifications/preferences');
                onClose?.();
              }}
              className="icon-button"
              title="Notification Preferences"
              aria-label="Notification Preferences"
            >
              <Settings className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="icon-button icon-button-sm"
              title="Close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-h-[420px] overflow-y-auto overscroll-contain">
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState />
        ) : notifications.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* New (Unread) Section */}
            {groupedNotifications.newItems.length > 0 && (
              <>
                <SectionHeader title="New" />
                <div className="divide-y divide-gray-200">
                  {groupedNotifications.newItems.map((notification) => (
                    <NotificationItem key={notification.id} notification={notification} />
                  ))}
                </div>
              </>
            )}

            {/* Earlier (Read) Section */}
            {groupedNotifications.earlierItems.length > 0 && (
              <>
                <SectionHeader title="Earlier" />
                <div className="divide-y divide-gray-200">
                  {groupedNotifications.earlierItems.map((notification) => (
                    <NotificationItem key={notification.id} notification={notification} />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
      
      {/* Footer */}
      {notifications.length > 0 && (
        <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50/50">
          <button 
            onClick={() => {
              navigate('/notifications');
              onClose?.();
            }}
            className="w-full text-center text-xs text-gray-600 hover:text-gray-900 font-medium transition-colors py-1"
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
